import React, { useMemo, useEffect, useState } from "react";
import { Col } from "react-bootstrap";
import { useSelector, shallowEqual } from "react-redux";
import { Link } from "react-router-dom";
import { Grid, Card, CardActionArea, Typography, Box } from "@material-ui/core";
import { useMediaQuery } from "@material-ui/core";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../_metronic/_helpers";
import getMenuConfig from "../../_metronic/layout/components/aside/aside-menu/menuConfig";
import { getDatabaseNamePath, isAgente } from "../config/config";
import Calendar from "../modules/Calendario/Calendar";
import Clock from "../pages/Common/Clock.jsx";

// API (accorpo tutte le chiamate API in un unico oggetto)
import { TrackingCodeButton } from "./Common/TrackingCodeInput.jsx";
import Order from "../models/order.ts";

import * as apiFile from "../api/file";
import * as apiRichieste from "../api/richieste";
import * as apiTracking from "../api/tracking";
import * as apiCalendario from "../api/calendario";
import Evento from "../models/event.ts";
import { ContactlessOutlined } from "@material-ui/icons";
import { roles } from "../config/config";

const apis = {
  file: apiFile,
  richieste: apiRichieste,
  tracking: apiTracking,
  calendario: apiCalendario,
};

const Tile = ({ header, title, icon, url, style }) => (
  <Grid item xs={12} sm={4} md={4}>
    <Card elevation={6} style={{ borderRadius: 16 }}>
      <CardActionArea component={Link} to={url} style={{ padding: 24, ...style }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          style={{ minHeight: 150 }}
        >
          <Typography
            variant="subtitle2"
            align="center"
            style={{ fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            {title}
          </Typography>
          <Box mb={2} mt={2}>
            <Box
              style={{
                width: 56,
                height: 56,
                borderRadius: 56,
                background: "rgba(255,255,255,0.22)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "saturate(180%) blur(2px)",
              }}
            >
              <SVG
                src={toAbsoluteUrl(icon)}
                style={{ width: 42, height: 42, filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.25))" }}
                className="svg-icon"
              />
            </Box>
          </Box>
          <Typography
            variant="subtitle2"
            align="center"
            style={{ fontWeight: 600, color: "#fff", textTransform: "uppercase", letterSpacing: 0.1 }}
          ></Typography>
        </Box>
      </CardActionArea>
    </Card>
  </Grid>
);

export function DashboardPage() {
  const isDesktop = useMediaQuery("(min-width:960px)");
  const user = useSelector(({ auth }) => auth.user, shallowEqual);
  const role = parseInt(user?.idRole, 10);
  const userIsAgente = isAgente(role);

  const userIsAdminOrDeveloper = role === roles.admin || role === roles.gestore || role === roles.developer;
  const userIsMagazziniere = role === roles.magazziniere;

  const menuItems = useMemo(() => getMenuConfig(userIsAgente, user), [userIsAgente, user]);

  const [nextEvent, setNextEvent] = useState(new Evento());
  const [hasEventsThisYear, setHasEventsThisYear] = useState(false);

  const [orders, setOrders] = useState([]); //Imposta un Array di Order
  const [lastOrder, setLastOrder] = useState(new Order());
  const [lastRejectedOrder, setLastRejectedOrder] = useState(new Order());

  const formatDateForApi = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  useEffect(() => {
    if (!user?.authToken) return;

    apis.calendario.nextEvent(user.authToken).then((res) => {
      setNextEvent(new Evento(res));
    });

    apis.richieste.getAll(user.authToken).then((res) => {
      setOrders(res.map((o) => new Order(o))); //Converto ogni oggetto in Order
    });

    apis.richieste.retrieveLastActiveOrder(user.authToken).then((res) => {
      setLastOrder(res);
    });
    apis.richieste.retrieveLastRejectedOrder(user.authToken).then((res) => {
      setLastRejectedOrder(res);
    });

    // Verifica se esistono eventi nell'anno corrente
    const year = new Date().getFullYear();
    const from = formatDateForApi(new Date(year, 0, 1));
    const to = formatDateForApi(new Date(year, 11, 31));
    apis.calendario
      .countEvents(user.authToken, from, to)
      .then((events) => {
        console.log("events:", events);
        setHasEventsThisYear(events > 0);
      })
      .catch(() => setHasEventsThisYear(false));
  }, [user]);

  const campioniServizi = menuItems.find((m) => m.title === "Campioni e Servizi");
  const eventi = menuItems.find((m) => m.title === "Eventi");
  const riepiloghi = menuItems.find((m) => m.title === "Riepiloghi");

  const mainRequested = (campioniServizi?.items || []).filter((si) =>
    ["Campioni", "Servizi", "Materiale Pubblicitario"].includes(si.title)
  );

  const eventiItems = eventi
    ? [{ title: eventi.title, url: eventi.url, icon: eventi.icon, allowedRoles: eventi.allowedRoles }]
    : [];

  const riepiloghiItems = riepiloghi?.items || [];

  // Override label dei titoli mostrati nelle tile
  const titleOverrides = {
    "/richiesta-campioni/": "Richiesta",
    "/servizi/": "Richiesta",
    "/richiesta-materiale/": "Richiesta",
    "/eventi": "Eventi",
    "/status-campionatura-servizi/": "Riepilogo",
    "/status-campionatura-servizi/#/materiale-pubblicitario": "Riepilogo",
    "/status-campionatura-servizi/#/servizi": "Riepilogo",
  };

  const getLabel = (item) => titleOverrides[item.url] || item.title;

  // Stili carini per tile (gradienti per categoria)
  const tileStylesByUrl = {
    "/richiesta-campioni/": { background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)" },
    "/servizi/": { background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)" },
    "/richiesta-materiale/": { background: "linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)" },
    "/eventi": { background: "linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)" },
    "/status-campionatura-servizi/": { background: "linear-gradient(135deg, #00b4db 0%, #0083b0 100%)" },
    "/status-campionatura-servizi/#/materiale-pubblicitario": {
      background: "linear-gradient(135deg, #ff758c 0%, #ff7eb3 100%)",
    },
    "/status-campionatura-servizi/#/servizi": { background: "linear-gradient(135deg, #3a7bd5 0%, #6a277eff 100%)" },
  };
  const getTileStyle = (item) =>
    tileStylesByUrl[item.url] || { background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" };

  // const requestsTiles = [...mainRequested, ...eventiItems].filter((item) => {
  const requestsTiles = [...mainRequested].filter((item) => {
    const allowed = item.allowedRoles || [];
    return !allowed.length || allowed.includes(role) || allowed.includes(true);
  });
  const summaryTiles = [...riepiloghiItems].filter((item) => {
    const allowed = item.allowedRoles || [];
    return !allowed.length || allowed.includes(role) || allowed.includes(true);
  });

  return (
    <>
      <Grid container spacing={2} p={0}>
        <Grid container item xs={12} md={hasEventsThisYear ? 6 : 12} lg={hasEventsThisYear ? 6 : 12} spacing={2}>
          {/* Box: Saluto e prossimo evento */}
          <Grid item xs={12} md={12} lg={12}>
            <Card elevation={3} style={{ borderRadius: 16 }}>
              <Box py={2} px={2}>
                <Grid container alignItems="flex-start">
                  <Col xs={12} className="pl-0">
                    <Box mb={2} p={0} alignContent="center" className="text-left">
                      <Typography variant="h6" style={{ fontWeight: 700 }}>
                        Ciao, {user?.firstname || ""}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Oggi è il{" "}
                        {new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })},
                        ore: <Clock />
                      </Typography>
                    </Box>

                    {nextEvent?.dataInizio ? (
                      <Typography variant="body1" color="textSecondary">
                        Prossimo evento: {nextEvent?.dataInizio || ""}
                      </Typography>
                    ) : (
                      <Typography variant="body1" color="textSecondary">
                        Non ci sono eventi futuri
                      </Typography>
                    )}
                    <Typography variant="body1" color="textPrimary">
                      {nextEvent?.title || ""}
                    </Typography>
                  </Col>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* Box: Stato degli ordini */}
          {isAgente(role) && (
            <Grid item xs={12} md={12} lg={12}>
              <Card elevation={3} style={{ borderRadius: 16 }}>
                <Box p={2}>
                  <Typography variant="h6" style={{ fontWeight: 700 }}>
                    Stato ordini
                  </Typography>
                </Box>
                <Box px={2} pb={2}>
                  {lastOrder.dateCreated < lastRejectedOrder.dateCreated && (
                    <Box display="flex" alignItems="center" justifyContent="space-between" className="p-0">
                      <Col xs={7} className="pl-0">
                        <Typography variant="body1" color="textSecondary">
                          Stato della richiesta campionatura {parseInt(lastRejectedOrder.extra) === 1 ? "EXTRA" : ""}{" "}
                          del {new Date(lastRejectedOrder?.dateCreated).toLocaleDateString("it-IT") || ""}
                        </Typography>
                      </Col>

                      <Col xs={5} className="pr-0">
                        <Typography variant="body1" color="textSecondary">
                          <i className="fas fa-check-circle me-2" style={{ color: "darkred" }}></i>{" "}
                          <b style={{ color: "darkred" }}>{lastRejectedOrder?.statusText}</b>
                        </Typography>
                      </Col>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center" justifyContent="space-between" className="p-0">
                    <Col xs={7} className="pl-0">
                      <Typography variant="body1" color="textSecondary">
                        Stato della richiesta campionatura {parseInt(lastOrder.extra) === 1 ? "EXTRA" : ""} del{" "}
                        {new Date(lastOrder?.dateCreated).toLocaleDateString("it-IT") || ""}
                      </Typography>
                    </Col>

                    <Col xs={5} className="pr-0">
                      <Typography variant="body1" color="textSecondary">
                        <i className="fas fa-check-circle me-2" style={{ color: "green" }}></i>{" "}
                        <b style={{ color: "green" }}>{lastOrder?.statusText}</b>
                      </Typography>
                    </Col>
                  </Box>

                  {lastOrder?.statusText === "evaso" && (
                    <Box display="flex" alignItems="center" justifyContent="space-between" className="p-0">
                      <Col xs={7} className="pl-0">
                        <Typography variant="body1" color="textSecondary">
                          Monitora la tua ultima spedizione
                        </Typography>
                      </Col>
                      <Col xs={5} className="pr-0">
                        <TrackingCodeButton trackingCode={lastOrder?.trackingCode} />
                      </Col>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          )}
          {(userIsAdminOrDeveloper || userIsMagazziniere) && (
            <Grid item xs={12} md={12} lg={12}>
              <Card elevation={3} style={{ borderRadius: 16 }}>
                <Box p={2}>
                  <Typography variant="h6" style={{ fontWeight: 700 }}>
                    Campionatura in sospeso
                  </Typography>
                </Box>
                <Box px={2} pb={2}>
                  {orders.filter((o) => o.status !== 0).length === 0 ? (
                    <Typography variant="body1" color="textSecondary">
                      Non ci sono ordini in attesa
                    </Typography>
                  ) : (
                    <Typography variant="body1" color="textSecondary">
                      Ci sono ordini in attesa di essere revisionati.
                      <br />
                      <a
                        href={`/${getDatabaseNamePath()}/status-campionatura-servizi/`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Vai al riepilogo ordini per visualizzarli
                      </a>
                    </Typography>
                  )}
                </Box>
              </Card>
            </Grid>
          )}
          {/* Box: Richieste */}
          {requestsTiles.length > 0 && (
            <Grid item xs={12} md={12} lg={12}>
              <Card elevation={3} style={{ borderRadius: 16 }}>
                <Box p={2}>
                  <Typography variant="h6" style={{ fontWeight: 700 }}>
                    Richieste
                  </Typography>
                </Box>
                <Box px={2} pb={2}>
                  <Grid container spacing={2}>
                    {requestsTiles.map((item) => (
                      <Tile
                        key={item.url}
                        header={getLabel(item)}
                        title={item.title}
                        icon={item.icon}
                        url={item.url}
                        style={getTileStyle(item)}
                      />
                    ))}
                  </Grid>
                </Box>
              </Card>
            </Grid>
          )}

          {/* Box: Riepiloghi */}
          <Grid item xs={12} md={12} lg={12}>
            <Card elevation={3} style={{ borderRadius: 16 }}>
              <Box p={2}>
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                  Riepiloghi
                </Typography>
              </Box>
              <Box px={2} pb={2}>
                <Grid container spacing={2}>
                  {summaryTiles.map((item) => (
                    <Tile
                      key={item.url}
                      header={getLabel(item)}
                      title={item.title}
                      icon={item.icon}
                      url={item.url}
                      style={getTileStyle(item)}
                    />
                  ))}
                </Grid>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Box: Agenda (solo se ci sono eventi nell'anno corrente) */}
        {hasEventsThisYear && (
          <Grid item xs={12} md={6} lg={6}>
            <Card elevation={3} style={{ borderRadius: 16 }}>
              <Box p={2}>
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                  Agenda
                </Typography>
              </Box>
              <Box px={2} pb={2}>
                <Calendar passedView={"listYear"} passedHeight={isDesktop ? "50vh" : "200px"} />
              </Box>
            </Card>
          </Grid>
        )}
      </Grid>
    </>
  );
}
